import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import {
  getTerraFlowProjectById,
  getTerraFlowProjects,
  getProjectCustomerLink,
  getProjectObjectLinks,
  getProjectAssignableObjects,
  createTibObjectFromProject,
  linkExistingObjectToProject,
  removeProjectObjectLink,
  acknowledgeProjectChanges,
  deleteTerraFlowProject,
  subscribeToDDLByDropdownId,
} from '../utils/storage';
import { stripClientId } from './CustomersPage';
import LoadingScreen from './LoadingScreen';
import { DetailPageHeader, ModalTemplate } from './templates';
import './TerraFlowManager.css';
import './InspectionStatus.css';
import './Modal.css';

// Scope disciplines in display order. Each has a matching `<key>_notes` field.
const SCOPE_FIELDS = [
  ['visual',               'Visual'],
  ['ultrasonic',           'Ultrasonic'],
  ['annular_projection',   'Annular projection'],
  ['scan_3d',              '3D scan'],
  ['deformation_analysis', 'Deformation analysis'],
  ['mt_survey',            'MT survey'],
  ['pt_survey',            'PT survey'],
  ['thermal_inspection',   'Thermal inspection'],
  ['coating_thickness',    'Coating thickness'],
  ['mfl_floorscan',        'MFL floorscan'],
];

function ScopeCell({ scope }) {
  const s = scope || {};
  return (
    <div className="tf-scope">
      {SCOPE_FIELDS.map(([key, label]) => {
        const on = s[key] === true;
        const note = (s[`${key}_notes`] || '').trim();
        return (
          <div key={key} className={`tf-scope-item${on ? ' on' : ''}`}>
            <span className="tf-scope-mark">{on ? '✓' : '✗'}</span>
            <span className="tf-scope-label">{label}</span>
            {note && <span className="tf-scope-note">— {note}</span>}
          </div>
        );
      })}
      {(s.mfl_classification || '').trim() && (
        <div className="tf-scope-extra">MFL classification: {s.mfl_classification}</div>
      )}
      {(s.additional_items || '').trim() && (
        <div className="tf-scope-extra">Additional items: {s.additional_items}</div>
      )}
    </div>
  );
}

export default function TerraFlowProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { setBreadcrumb, clearBreadcrumb } = useBreadcrumb();

  const [project, setProject] = useState(null);
  const [link, setLink] = useState(null);          // customer→test-site link
  const [links, setLinks] = useState({});           // ogf_object_ref → { object, inspections }
  const [linkLoading, setLinkLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageReady, setPageReady] = useState(false);
  const [createFor, setCreateFor] = useState(null); // OGF object being created into TIB
  const [assignFor, setAssignFor] = useState(null);  // OGF object being linked to an existing TIB object
  const [removeFor, setRemoveFor] = useState(null);  // { ref, tag } pending link removal
  const [neighbours, setNeighbours] = useState({ prevId: null, nextId: null });
  const [changesAck, setChangesAck] = useState(false); // hide changes modal after confirm
  const [showDelete, setShowDelete] = useState(false);  // delete-project confirm modal
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await getTerraFlowProjectById(projectId);
      if (!cancelled) { setProject(p); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // Prev/next chevrons follow the same order as the TerraFlow projects table.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await getTerraFlowProjects();
      if (cancelled) return;
      const idx = list.findIndex((x) => String(x.id) === String(projectId));
      setNeighbours({
        prevId: idx > 0 ? list[idx - 1].id : null,
        nextId: idx >= 0 && idx < list.length - 1 ? list[idx + 1].id : null,
      });
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const refreshLink = useCallback(async () => {
    setLinkLoading(true);
    try { setLink(await getProjectCustomerLink(projectId)); }
    finally { setLinkLoading(false); }
  }, [projectId]);

  const refreshObjectLinks = useCallback(async () => {
    setLinks(await getProjectObjectLinks(projectId));
  }, [projectId]);

  useEffect(() => { refreshLink(); refreshObjectLinks(); }, [refreshLink, refreshObjectLinks]);

  useEffect(() => {
    if (project) {
      setBreadcrumb([
        { label: 'TerraFlow', path: '/terraflow' },
        { label: project.name || 'Project', path: null },
      ]);
    }
    return () => clearBreadcrumb();
  }, [project, setBreadcrumb, clearBreadcrumb]);

  if (!pageReady) {
    return <LoadingScreen isLoading={loading} onDone={() => setPageReady(true)} />;
  }
  if (!project) {
    return (
      <div className="page-container">
        <button className="btn-back" onClick={() => navigate('/terraflow')}>&larr; Back to TerraFlow</button>
        <p style={{ marginTop: 'var(--space-lg)' }}>Project not found.</p>
      </div>
    );
  }

  const ogf = project.rawPayload || {};
  const pinfo = ogf.project_info || {};
  const reporting = ogf.reporting || {};
  const objects = Array.isArray(ogf.objects) ? ogf.objects : [];
  const contactPerson = (pinfo.contact_planning || {}).name || '';

  const clientCode = link?.client_code || (ogf.project_number || '').split('.')[0];
  const clientIdDisplay = stripClientId(clientCode) || '—';
  const ts = link?.test_site;
  const linked = !!link?.linked;
  const linkMsg = link && !link.customer_found
    ? 'No matching customer found for this project.'
    : 'No test site linked to this customer yet.';

  async function handleRemoveConfirmed() {
    const ref = removeFor?.ref;
    setRemoveFor(null);
    if (ref == null) return;
    await removeProjectObjectLink(projectId, ref);
    refreshObjectLinks();
  }

  async function handleConfirmChanges() {
    setChangesAck(true);
    try { await acknowledgeProjectChanges(projectId); } catch { /* best-effort ack */ }
    const p = await getTerraFlowProjectById(projectId);   // refresh so the dot/count clear
    if (p) setProject(p);
  }

  async function handleDeleteConfirmed() {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteTerraFlowProject(projectId);
      navigate('/terraflow');
    } catch {
      setDeleting(false);   // stay on the page so the user can retry
    }
  }

  return (
    <div className="page-container">
      <DetailPageHeader
        title={ogf.project_name || project.name || 'Project'}
        onBack={() => navigate('/terraflow')}
        backLabel="← Back to TerraFlow"
        onPrev={() => neighbours.prevId && navigate(`/terraflow/project/${neighbours.prevId}`)}
        onNext={() => neighbours.nextId && navigate(`/terraflow/project/${neighbours.nextId}`)}
        canPrev={!!neighbours.prevId}
        canNext={!!neighbours.nextId}
        prevTitle="Previous project"
        nextTitle="Next project"
      >
        {/* ── Project information (inside header → tight gap, matches object page) ── */}
        <div className="detail-info">
          <div className="od-info-header-row">
            <div className="od-info-title" style={{ marginBottom: 0 }}>Project Information</div>
            <button className="btn btn-danger btn-sm" onClick={() => setShowDelete(true)}>
              Delete project
            </button>
          </div>
          <p>Project number: {ogf.project_number || '—'}</p>
          <p>Account manager: {pinfo.account_manager || '—'}</p>
          <p>Object type: {ogf.inspection_type || '—'}</p>
          <p>Execution date: {pinfo.execution_date || '—'}</p>
          <p>Reporting language: {reporting.language || '—'}</p>
          <p>Inspection standard: {reporting.standard || '—'}</p>
        </div>
      </DetailPageHeader>

      {/* ── Customer information ────────────────────────────────── */}
      <div className="detail-info tf-card">
        <div className="od-info-header-row">
          <div className="od-info-title" style={{ marginBottom: 0 }}>Customer Information</div>
          <button className="btn btn-secondary btn-sm" onClick={refreshLink} disabled={linkLoading}>
            {linkLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <p>Client ID: {clientIdDisplay}</p>
        {ts ? (
          <>
            <p>Client name: {ts.name}</p>
            <p>Address: {[ts.address_line, ts.post_code, ts.city, ts.country].filter(Boolean).join(', ') || '—'}</p>
          </>
        ) : (
          <>
            <p>Client name: <span className="tf-unlinked">{linkMsg}</span></p>
            <p>Address: <span className="tf-unlinked">{linkMsg}</span></p>
          </>
        )}
        <p>Contact person: {contactPerson || '—'}</p>
      </div>

      {/* ── Objects from TerraFlow ──────────────────────────────── */}
      <div className="tf-linked-section">
        <div className="od-info-title">Objects from TerraFlow ({objects.length})</div>
        {objects.length === 0 ? (
          <p className="tf-unlinked">No objects in this project’s TerraFlow data.</p>
        ) : (
          <div className="table-wrapper">
            <table className="ds-table ds-table-fixed tf-obj-table">
              <colgroup>
                <col className="tf-obj-col-id" />
                <col className="tf-obj-col-scope" />
                <col className="tf-obj-col-status" />
              </colgroup>
              <thead>
                <tr>
                  <th>Object ID</th>
                  <th>Scope</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {objects.map((o, i) => {
                  const ref = o.id_number || `#${i}`;
                  const li = links[ref];
                  return (
                    <tr key={i}>
                      <td className="tf-obj-id">{o.id_number || '—'}</td>
                      <td><ScopeCell scope={o.scope} /></td>
                      <td>
                        <StatusCell
                          linkInfo={li}
                          canCreate={linked}
                          onCreate={() => setCreateFor({ ref, tag: o.id_number || '' })}
                          onAssign={() => setAssignFor({ ref, tag: o.id_number || '' })}
                          onRemove={() => setRemoveFor({ ref, tag: o.id_number || '' })}
                          onOpenObject={(obj) => navigate(`/client/${obj.client_id}/object/${obj.id}`)}
                          onOpenInspection={(obj, insp) =>
                            navigate(`/client/${obj.client_id}/object/${obj.id}/inspection/${insp.id}`)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {createFor && (
        <CreateTibObjectModal
          projectId={projectId}
          ogfObjectRef={createFor.ref}
          initialTag={createFor.tag}
          onClose={() => setCreateFor(null)}
          onCreated={() => { setCreateFor(null); refreshObjectLinks(); }}
        />
      )}

      {assignFor && (
        <AssignExistingObjectModal
          projectId={projectId}
          ogfObjectRef={assignFor.ref}
          matchTarget={{
            accountManager: pinfo.account_manager || '',
            startDate: pinfo.execution_date || '',
            inspectionNumber: ogf.project_number || '',
          }}
          onClose={() => setAssignFor(null)}
          onAssigned={() => { setAssignFor(null); refreshObjectLinks(); }}
        />
      )}

      {Array.isArray(project.pendingChanges) && project.pendingChanges.length > 0 && !changesAck && (
        <ProjectChangesModal changes={project.pendingChanges} onConfirm={handleConfirmChanges} />
      )}

      {showDelete && (
        <div className="modal-overlay" onClick={() => !deleting && setShowDelete(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Delete this project?</h2>
            <p className="modal-body-text">
              This removes the project from the app entirely. Any linked TIB objects and their
              inspections are <strong>kept</strong> — only the project and its object links are
              removed. If TerraFlow still has this project, a future sync may re-create it.
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowDelete(false)} disabled={deleting}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteConfirmed} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {removeFor && (
        <div className="modal-overlay" onClick={() => setRemoveFor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Remove TIB object link?</h2>
            <p className="modal-body-text">
              This unlinks the TIB object from <strong>{removeFor.tag || 'this object'}</strong>. The TIB
              object itself is kept — only the link is removed.
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setRemoveFor(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleRemoveConfirmed}>Remove link</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Status cell (per object row) ─────────────────────────────────── */
function StatusCell({ linkInfo, canCreate, onCreate, onAssign, onRemove, onOpenObject, onOpenInspection }) {
  if (linkInfo) {
    const obj = linkInfo.object;
    const insps = linkInfo.inspections || [];
    return (
      <div className="tf-status-linked">
        <div className="tf-linked-obj-row">
          <button className="tf-linked-obj" onClick={() => onOpenObject(obj)} title="Open TIB object">
            {obj.object_tag || 'TIB object'}
          </button>
          {obj.archived && <span className="tf-archived-badge">Object archived</span>}
        </div>
        {insps.length === 0 ? (
          <div className="tf-unlinked">No inspections yet.</div>
        ) : (
          insps.map((ins) => (
            <div key={ins.id} className="tf-insp-row" onClick={() => onOpenInspection(obj, ins)}>
              <div className="tf-insp-line">
                <span className="tf-insp-code">{ins.code || ins.type || 'Inspection'}:</span>
                <span className="insp-status-badge" data-status={ins.effectiveStatus}>{ins.effectiveStatus}</span>
              </div>
              <div className="tf-insp-line">
                <span className="tf-insp-label">assigned to:</span>
                <span className="tf-insp-assigned">{ins.assigned || '—'}</span>
              </div>
            </div>
          ))
        )}
        {!obj.archived && (
          <button className="btn btn-danger btn-sm" onClick={onRemove}>Remove TIB Object</button>
        )}
      </div>
    );
  }
  if (canCreate) {
    return (
      <div className="tf-obj-actions">
        <button className="btn btn-primary btn-sm" onClick={onCreate}>Create TIB Object</button>
        <button className="btn btn-secondary btn-sm" onClick={onAssign}>Assign TIB Object</button>
      </div>
    );
  }
  return <span className="tf-unlinked">Link a test site to the customer first.</span>;
}

/* ── Create-TIB-object modal (Add Object, prefilled) ──────────────── */
function CreateTibObjectModal({ projectId, ogfObjectRef, initialTag, onClose, onCreated }) {
  const [objectType, setObjectType] = useState('');
  const [objectId, setObjectId] = useState(initialTag || '');
  const [typeOptions, setTypeOptions] = useState([]);
  const [ddlLoading, setDdlLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = subscribeToDDLByDropdownId('object_type', (labels) => {
      setTypeOptions(labels); setDdlLoading(false);
    });
    return () => unsub();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!objectType || !objectId.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      await createTibObjectFromProject(projectId, {
        ogfObjectRef, type: objectType, objectTag: objectId.trim(),
      });
      onCreated();
    } catch (err) {
      setError(err?.message || 'Failed to create object.');
      setSaving(false);
    }
  }

  return (
    <ModalTemplate title="New Object" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <p className="confirm-text">
          Created under the project’s linked test site. Account manager, start date and inspection
          number are filled in automatically from the project.
        </p>
        <div className="form-group">
          <label htmlFor="tibObjType">Object Type <span className="required-star">*</span></label>
          {ddlLoading ? (
            <div className="ddl-load-hint">Loading types…</div>
          ) : typeOptions.length > 0 ? (
            <select id="tibObjType" className="assign-select-full" value={objectType}
              onChange={(e) => { setObjectType(e.target.value); setError(null); }} required autoFocus>
              <option value="">— Select a type —</option>
              {typeOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          ) : (
            <div className="ddl-no-options-hint">
              No types configured. Assign a DDL collection to <strong>Object — Type</strong> first.
            </div>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="tibObjId">Object ID <span className="required-star">*</span></label>
          <input id="tibObjId" type="text" value={objectId}
            onChange={(e) => { setObjectId(e.target.value); setError(null); }}
            placeholder="e.g. TK-101" required />
        </div>
        {error && <div className="alert alert-danger" style={{ marginTop: 12 }}>{error}</div>}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={!objectType || !objectId.trim() || saving}>
            {saving ? 'Creating…' : 'Create Object'}
          </button>
        </div>
      </form>
    </ModalTemplate>
  );
}

/* ── Assign-existing-object modal ─────────────────────────────────── */
// Lists existing objects from the project's linked test site. An object may
// only be linked when its account manager, start date and project number all
// match the project (the same 3 fields create-tib trickles down). On mismatch
// the warning is shown and Assign stays disabled — hard block, no override.
function AssignExistingObjectModal({ projectId, ogfObjectRef, matchTarget, onClose, onAssigned }) {
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await getProjectAssignableObjects(projectId);
      if (!cancelled) { setObjects(list); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const norm = (v) => String(v || '').trim().toLowerCase();
  const selected = objects.find((o) => o.id === selectedId) || null;
  const mismatches = selected
    ? [
        ['Account manager', selected.generalInfo.accountManager, matchTarget.accountManager],
        ['Start date',      selected.generalInfo.startDate,      matchTarget.startDate],
        ['Project number',  selected.generalInfo.inspectionNumber, matchTarget.inspectionNumber],
      ].filter(([, ov, pv]) => norm(ov) !== norm(pv))
    : [];
  const canAssign = !!selected && mismatches.length === 0 && !saving;

  async function handleAssign() {
    if (!canAssign) return;
    setSaving(true); setError(null);
    try {
      await linkExistingObjectToProject(projectId, { ogfObjectRef, objectId: selected.id });
      onAssigned();
    } catch (err) {
      setError(err?.message || 'Failed to assign object.');
      setSaving(false);
    }
  }

  return (
    <ModalTemplate title="Assign TIB Object" onClose={onClose}>
      <p className="confirm-text">
        Link an existing object from this test site. Its account manager, start date and project
        number must match this project.
      </p>

      {loading ? (
        <div className="ddl-load-hint">Loading objects…</div>
      ) : objects.length === 0 ? (
        <p className="confirm-text">No objects found for this test site.</p>
      ) : (
        <div className="site-select-list">
          {objects.map((o) => {
            const isSel = o.id === selectedId;
            const isLinked = !!o.already_linked_ref;
            return (
              <div
                key={o.id}
                className={`site-select-item${isSel ? ' tf-pick-selected' : ''}${isLinked ? ' tf-pick-disabled' : ''}`}
                onClick={() => !isLinked && setSelectedId(o.id)}
              >
                <div style={{ flex: 1 }}>
                  <div className="site-select-item-name">{o.object_tag || '(unnamed)'}</div>
                  {o.type && <div className="site-select-item-meta">{o.type}</div>}
                </div>
                {isLinked
                  ? <span className="tf-pick-added" title="Already linked">✓</span>
                  : isSel
                    ? <span className="tf-pick-check" title="Selected">✓</span>
                    : <span className="site-select-item-arrow">›</span>}
              </div>
            );
          })}
        </div>
      )}

      {selected && mismatches.length > 0 && (
        <div className="alert alert-danger tf-assign-warning">
          <strong>Cannot assign — assessment info does not match the project:</strong>
          <ul>
            {mismatches.map(([label, ov, pv]) => (
              <li key={label}>{label}: object “{ov || '—'}” vs project “{pv || '—'}”</li>
            ))}
          </ul>
        </div>
      )}
      {error && <div className="alert alert-danger" style={{ marginTop: 12 }}>{error}</div>}

      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={handleAssign} disabled={!canAssign}>
          {saving ? 'Assigning…' : 'Assign'}
        </button>
      </div>
    </ModalTemplate>
  );
}

/* ── "What changed" modal (webhook updates) ───────────────────────── */
// Shown the first time a user opens a project after TerraFlow pushed changes.
// Click-outside is intentionally disabled (no overlay onClick) — the user must
// press "Confirm and close" to acknowledge.
function ProjectChangesModal({ changes, onConfirm }) {
  const [saving, setSaving] = useState(false);
  async function confirm() {
    if (saving) return;
    setSaving(true);
    await onConfirm();
  }
  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">This project was updated in TerraFlow</h2>
        <p className="modal-body-text">
          The following {changes.length === 1 ? 'change was' : 'changes were'} received from TerraFlow.
          Account manager, start date and project number are applied to the linked object automatically.
        </p>
        <ul className="tf-changes-list">
          {changes.map((c, i) => (
            <li key={i}>
              <span className="tf-change-label">{c.label}:</span>{' '}
              <span className="tf-change-old">{c.old || '—'}</span>
              <span className="tf-change-arrow"> → </span>
              <span className="tf-change-new">{c.new || '—'}</span>
            </li>
          ))}
        </ul>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={confirm} disabled={saving}>
            {saving ? 'Saving…' : 'Confirm and close'}
          </button>
        </div>
      </div>
    </div>
  );
}
